import AdvancedController from "./advanced-controller";
import { Breakpoints } from "../utils/breakpoints";
import { debounce } from "../utils/utils";
import Hunt from "huntjs";
import Swiper, { Navigation, Pagination, Autoplay } from "swiper";

const AUTO_HEIGHT_BREAKPOINT = "sm";

export default class SwiperController extends AdvancedController {
  static targets = ["swiper"];

  static values = {
    autoplay: Boolean,
    sameheight: Boolean,
    onlyautoheightmobile: Boolean,
    maxslides: Boolean
  };

  connect() {
    super.connect();
    this.createSlider(this.swiperTarget, !this.sameheightValue);
    this.breakpoints = new Breakpoints();

    //disable autoslide on start, as it gets handled by huntjs if in viewport
    if (this.hasSwiperTarget && this.onlyautoheightmobileValue) {
      this.manageAutoHeight();
      window.addEventListener("resize", () =>
        debounce(this.manageAutoHeight(), 250)
      );
    }

    this.disableAutoswipe();
    new Hunt(this.element, {
      persist: true,
      enter: () => this.enableAutoswipe(),
      leave: () => this.disableAutoswipe(),
    });
  }

  enableAutoswipe() {
    // class instead of prop needs to be checked because autoplay prop is also on non-autoplay instances
    if (
      this.hasSwiperTarget &&
      this.autoplayValue &&
      !this.swiperTarget.swiper.autoplay.running
    ) {
      this.swiperTarget.swiper.autoplay.start();
    }
  }

  disableAutoswipe() {
    if (
      this.hasSwiperTarget &&
      this.autoplayValue &&
      this.swiperTarget.swiper &&
      this.swiperTarget.swiper.autoplay.running
    ) {
      this.swiperTarget.swiper.autoplay.stop();
    }
  }

  manageAutoHeight() {
    if (
      this.hasSwiperTarget &&
      this.breakpoints.breakpointDown(AUTO_HEIGHT_BREAKPOINT) &&
      !this.swiperTarget.swiper.params.autoHeight
    ) {
      this.destroyAndReinit(true);
    }
    if (
      this.hasSwiperTarget &&
      this.breakpoints.breakpointUp(AUTO_HEIGHT_BREAKPOINT) &&
      this.swiperTarget.swiper.params.autoHeight
    ) {
      this.destroyAndReinit(false);
    }
  }

  destroyAndReinit(autoHeight) {
    this.swiperTarget.swiper.destroy();
    this.createSlider(this.swiperTarget, autoHeight);
  }

  createSlider(element, autoHeight = false) {
    const slidesPerView = element.getAttribute("data-slides-per-view") || 1;

    const config = {
      modules: [Navigation, Pagination, Autoplay],
      slidesPerView: slidesPerView,
      grabCursor: true,
      pagination: {
        el: element.querySelector('.swiper-pagination'),
        bulletActiveClass: 'current',
        clickable: true,
        renderBullet: function (index, className) {
          return '<span class="' + className + '">' + (index + 1) + "</span>";
        },
      },
      navigation: {
        prevEl: element.querySelector(".swiper-button-prev"),
        nextEl: element.querySelector(".swiper-button-next"),
      },
    };

    const autoplay = !!+element.getAttribute("data-autoplay-enable");
    const autoplayDelay = element.getAttribute("data-autoplay-delay");
    if (autoplay) {
      config.autoplay = {
        delay: Number(autoplayDelay),
        pauseOnMouseEnter: true,
        disableOnInteraction: false,
      };
    }

    const spaceBetween = element.getAttribute("data-space-between");
    if (spaceBetween === "") {
      config.spaceBetween = 24; // --tc-gutter-x
    }

    const loop = !!+element.getAttribute("data-loop-enable");

    if (slidesPerView > 1) {
      const swiperBreakpoints = this.calculateSwiperBreakpoints(slidesPerView);
      config.breakpoints = swiperBreakpoints;
    }

    if (loop) {
      // const initialSlide = Math.floor(slidesPerView / 2);
      // config.initialSlide = initialSlide;
      // config.centeredSlides = true;
      config.loop = true;
      const totalSlides = element.querySelectorAll(".swiper-slide");

      // check if there are more slides than slidesPerView * 2
      // otherwise loop doesn't work
      if (totalSlides.length <= slidesPerView * 2) {
        // double all slides
        totalSlides.forEach((slide) => {
          element
            .querySelector(".swiper-wrapper")
            .appendChild(slide.cloneNode(true));
        });
        // turn off pagination bc doubled slides would show up as well
        config.pagination = false;
      }

      config.on = {
        init: function () {
          this.slideTo(Math.floor(this.slides.length / 2), 0);
        },
      };
    }

    if (autoHeight) {
      config.autoHeight = true;
    }

    this.swiper = new Swiper(element, config);
  }

  calculateSwiperBreakpoints(slidesPerView) {
    // this will be returned
    const swiperBreakpoints = {};
    const breakpointsService = new Breakpoints();
    const allBreakpoints = breakpointsService.getAll();
    // add xxl breakpoint
    allBreakpoints.xxl = 1600;

    // calculate the step size between each breakpoint, e.g. 8 / 4 = 2 (=> xs: 1, sm: 1+2, md: 1+2+2)
    const perBreakpoint =
      slidesPerView / (Object.keys(allBreakpoints).length + 1);

    // sort the breakpoints by pixel values for the loop
    const breakpointPixelValues = Object.values(allBreakpoints).sort(
      (a, b) => a - b
    );

    let i = 1;
    // force add a breakpoint for 0px
    swiperBreakpoints[0] = { slidesPerView: 1 };
    for (const breakpointSize of breakpointPixelValues) {
      i++;
      // calculate how much slides shall be displayed
      const slidesPerBreakpoint = Math.floor(1 + i * perBreakpoint);

      // add the config
      swiperBreakpoints[breakpointSize] = {
        slidesPerView: this.getSlidesPerBreakpoint(slidesPerBreakpoint, slidesPerView),
      };
    }

    return swiperBreakpoints;
  }

  getSlidesPerBreakpoint(slidesPerBreakpoint, slidesPerView) {
    if (this.maxslidesValue && slidesPerBreakpoint > slidesPerView) {
      slidesPerBreakpoint = slidesPerView;
    }

    return slidesPerBreakpoint;
  }
}
